import { useEffect } from "react";
import { CallEventAdapter, CallEvent } from "../types";
import { useSession } from "../context/SessionContext";
import { useAnalytics } from "../context/AnalyticsContext";
import { isGetBookingFn, mapBookingToProfile } from "../lib/copaProfile";

const uuidv4 = () => crypto.randomUUID();

export function useCallEvents(adapter: CallEventAdapter | null, callId: string | null) {
  const { state, dispatch } = useSession();
  const { track } = useAnalytics();

  useEffect(() => {
    if (!adapter || !callId) return;

    const onConnecting = () => dispatch({ type: "CALL_STATE_CHANGE", state: "connecting" });

    const onConnected = (e: Extract<CallEvent, { type: "call.connected" }>) => {
      dispatch({ type: "CALL_STATE_CHANGE", state: "in_call" });
      track("experience.call_started", { agent: state.agentSlug, channel: state.channel, callId, startedAt: e.startedAt });
    };

    const onEnded = (e: Extract<CallEvent, { type: "call.ended" }>) => {
      dispatch({ type: "CALL_STATE_CHANGE", state: "ended" });
      const duration = state.startedAt ? e.endedAt - state.startedAt : 0;
      track("experience.call_ended", { agent: state.agentSlug, channel: state.channel, callId, duration: Math.round(duration / 1000), reason: e.reason });
    };

    const onTranscript = (e: Extract<CallEvent, { type: "transcript.turn" }>) => {
      // Reuse the adapter's utterance id when it supplies one: re-emitting the
      // same id rewrites the bubble in place as the turn grows, instead of
      // appending a sibling for every fragment.
      dispatch({ type: "TRANSCRIPT_TURN", turn: { id: e.id ?? uuidv4(), speaker: e.speaker, text: e.text, timestampMs: e.timestampMs } });
    };

    const onToolCall = (e: Extract<CallEvent, { type: "tool_call" }>) => {
      // Tool calls render only in the right-side Live Action panel — not as
      // a transcript turn. Avoids cluttering the conversation flow with
      // wrench rows next to spoken turns.
      dispatch({
        type: "ADD_LIVE_ACTION",
        action: {
          id: uuidv4(),
          type: "tool_call",
          functionName: e.functionName,
          args: e.args,
          output: e.output,
          isError: e.isError,
          durationMs: e.durationMs,
          timestamp: e.timestampMs,
        },
      });

      // Copa: when the crew looks up the booking, populate the live passenger
      // profile card (name, tier, booking, refund status) from the tool output.
      if (!e.isError && isGetBookingFn(e.functionName)) {
        const passenger = mapBookingToProfile(e.output);
        if (passenger) dispatch({ type: "SET_LIVE_PASSENGER", passenger });
      }
    };

    const onLiveAction = (e: Extract<CallEvent, { type: "live_action" }>) => {
      dispatch({ type: "ADD_LIVE_ACTION", action: e.action });
      if (e.action.type === "processing") {
        dispatch({ type: "TRANSCRIPT_TURN", turn: { id: uuidv4(), speaker: "tool", text: e.action.label, timestampMs: Date.now() } });
      } else if (e.action.type === "api_connection") {
        dispatch({ type: "TRANSCRIPT_TURN", turn: { id: uuidv4(), speaker: "tool", text: e.action.title, timestampMs: Date.now() } });
      }
    };

    const onAgentHandoff = (e: Extract<CallEvent, { type: "agent.handoff" }>) => {
      // Handoffs are surfaced only via the Live Action panel's agent routing
      // strip — not as a transcript turn (keeps the conversation clean).
      // Treat a transfer into a human/representative agent as a warm transfer so
      // the routing strip shows the escalation styling (the backend reports the
      // active sub-agent id without a warm flag).
      const warm = e.isWarmTransfer || /human|representative|specialist|live\s*agent/i.test(e.toAgent);
      dispatch({ type: "AGENT_HANDOFF", fromAgent: e.fromAgent, toAgent: e.toAgent, routingCondition: e.routingCondition, isWarmTransfer: warm });
    };

    const onError = (e: Extract<CallEvent, { type: "error" }>) => {
      dispatch({ type: "SET_ERROR", message: e.message });
      dispatch({ type: "CALL_STATE_CHANGE", state: "failed" });
    };

    adapter.on("call.connecting", onConnecting);
    adapter.on("call.connected", onConnected);
    adapter.on("call.ended", onEnded);
    adapter.on("transcript.turn", onTranscript);
    adapter.on("tool_call", onToolCall);
    adapter.on("live_action", onLiveAction);
    adapter.on("agent.handoff", onAgentHandoff);
    adapter.on("error", onError);

    adapter.connect(callId);

    return () => {
      // Detach listeners but DO NOT call adapter.disconnect() here.
      // CallSurfaceStep's mount-effect owns the adapter lifecycle and will
      // disconnect on real unmount. Disconnecting here would tear down the
      // live call any time this effect re-runs (HMR, dep changes), which
      // is what made the agent appear to start "on its own".
      adapter.off("call.connecting", onConnecting);
      adapter.off("call.connected", onConnected);
      adapter.off("call.ended", onEnded);
      adapter.off("transcript.turn", onTranscript);
      adapter.off("tool_call", onToolCall);
      adapter.off("live_action", onLiveAction);
      adapter.off("agent.handoff", onAgentHandoff);
      adapter.off("error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, callId]);
}
